import { useCallback, useEffect, useReducer, useState } from "react";
import Form from "@src/components/forms/Form";
import { Checkbox, DropDown, FormInput, Loader } from "@src/components/atoms";
import Button from "@src/components/atoms/Button";
import moment from "moment";
import { targetSchema } from "../formHelpers";
import {
  normalizeEmptyStringsToNull,
  getEventTypeIdFromIndustry,
} from "./AdminHelpers";

export const TargetForm = ({
  isNew = false,
  industry,
  data = {},
  behaviours,
  onSubmit,
  onCancel,
}) => {
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [minBSRDate, setMinBSRDate] = useState(null);
  const [maxBSRDate, setMaxBSRDate] = useState(null);

  const behaviourOptions =
    behaviours?.filter((behaviour) => behaviour.enabled) || [];

  const initialTargetObject = {
    name: data?.name || "",
    behaviourTypeId: data?.behaviourTypeId || "",
    targetPercentage: data?.targetPercentage || null,
    startDate: data?.startDate || new Date().toISOString().split("T")[0],
    baselineSuccessRateStartDate: data?.baselineSuccessRateStartDate
      ? data.baselineSuccessRateStartDate.split("T")[0]
      : null,
    baselineSuccessRateEndDate: data?.baselineSuccessRateEndDate
      ? data.baselineSuccessRateEndDate.split("T")[0]
      : null,
    baselineEventCountEndDate: data?.baselineEventCountEndDate
      ? data.baselineEventCountEndDate.split("T")[0]
      : null,
  };

  const handleSubmit = async (data) => {
    console.log("form data", data);
    setSubmitting(true);
    setError(null);

    try {
      const cleanedData = normalizeEmptyStringsToNull(data);
      onSubmit && (await onSubmit(cleanedData));
    } catch (err) {
      console.log("Error submitting target form:", err);
      setError("Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isNew && !data?.name) {
    return <Loader />;
  }

  return (
    <div className="flex flex-col overflow-scroll">
      <Form
        onSubmit={(data) => {
          handleSubmit(data);
        }}
        schema={targetSchema}
        options={{ defaultValues: initialTargetObject }}
        className="space-y-6"
      >
        {(methods) => {
          const hasChanged = methods.formState.isDirty;
          return (
            <>
              <div className="mt-4 space-y-4 border-t pt-4">
                <FormInput
                  label="Target Name"
                  name="name"
                  value={methods.watch("name")}
                  onChange={(e) => {
                    methods.setValue("name", e.target.value, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }}
                />
                {behaviourOptions && (
                  <DropDown
                    label="Target Behaviour"
                    name="behaviourTypeId"
                    value={methods.watch("behaviourTypeId")}
                    options={behaviourOptions.map((behaviour) => ({
                      value: behaviour.behaviourTypeId,
                      text: `${behaviour.acronym} - ${behaviour.name}`,
                    }))}
                    onChange={(value) =>
                      methods.setValue("behaviourTypeId", value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  />
                )}
                <div className="border-1 rounded-sm border-gray-500/50 border-solid p-4">
                  <h6 className="mb-4 text-red-400">
                    Changing these can impact historical and current goals for
                    operators in this team.
                    <br />
                    Changes will not take effect until new data is processed
                    (~5am daily except for a full customer data outage).
                  </h6>
                  <FormInput
                    label="Target Percentage"
                    name="targetPercentage"
                    type="number"
                    value={methods.watch("targetPercentage")}
                    onChange={(e) => {
                      methods.setValue("targetPercentage", e.target.value, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                  />
                  <FormInput
                    label="Target Start Date"
                    name="startDate"
                    type="date"
                    info="Should be the start date of a goal period."
                    fValue={moment
                      .utc(methods.watch("startDate"))
                      .format("YYYY-MM-DD")}
                    onChange={(e) => {
                      methods.setValue("startDate", e.target.value, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                  />
                  <FormInput
                    label="Baseline success rate start date"
                    name="baselineSuccessRateStartDate"
                    type="date"
                    info="Overrides team baseline success rate start date for this behaviour"
                    maxDate={maxBSRDate}
                    fValue={
                      methods.watch("baselineSuccessRateStartDate")
                        ? moment
                            .utc(methods.watch("baselineSuccessRateStartDate"))
                            .format("YYYY-MM-DD")
                        : ""
                    }
                    onChange={(e) => {
                      setMinBSRDate(e.target.value);
                      methods.setValue(
                        "baselineSuccessRateStartDate",
                        e.target.value || null,
                        {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        },
                      );
                    }}
                  />
                  <FormInput
                    label="Baseline success rate end date"
                    name="baselineSuccessRateEndDate"
                    type="date"
                    info="Overrides team baseline success rate end date for this behaviour"
                    minDate={minBSRDate}
                    fValue={
                      methods.watch("baselineSuccessRateEndDate")
                        ? moment
                            .utc(methods.watch("baselineSuccessRateEndDate"))
                            .format("YYYY-MM-DD")
                        : ""
                    }
                    onChange={(e) => {
                      setMaxBSRDate(e.target.value);
                      methods.setValue(
                        "baselineSuccessRateEndDate",
                        e.target.value || null,
                        {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        },
                      );
                    }}
                  />
                  <FormInput
                    label="Baseline end date"
                    name="baselineEventCountEndDate"
                    type="date"
                    info="Overrides team baseline end date for this behaviour"
                    fValue={
                      methods.watch("baselineEventCountEndDate")
                        ? moment
                            .utc(methods.watch("baselineEventCountEndDate"))
                            .format("YYYY-MM-DD")
                        : ""
                    }
                    onChange={(e) => {
                      methods.setValue(
                        "baselineEventCountEndDate",
                        e.target.value || null,
                        {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        },
                      );
                    }}
                  />
                </div>
              </div>
              <Button submit loading={submitting}>
                Save changes
              </Button>
              {error && (
                <div className="mt-4 text-xs italic text-red-500">{error}</div>
              )}
            </>
          );
        }}
      </Form>{" "}
    </div>
  );
};
